/**
 * ChatWidget Component
 * Lightweight facade for Tawk.to chat widget with lazy loading
 * Improves LCP by deferring heavy script load until interaction or idle time
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { tawkToLoader } from '../utils/tawkToLoader';
import logger from '../utils/logger.js';

const ChatWidget = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isMinimized, setIsMinimized] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();

  // Routes where chat should not appear
  const excludedRoutes = ['/popup', '/preview'];
  const shouldShowChat = !excludedRoutes.some(route => location.pathname.includes(route));

  // Load Tawk.to script
  const loadTawkTo = useCallback(async () => {
    if (isLoaded || isLoading) return;

    setIsLoading(true);
    setError(null);

    try {
      const success = await tawkToLoader.load();
      if (success) {
        setIsLoaded(true);
        logger.info('ChatWidget: Tawk.to loaded successfully', null, 'chat');
        
        // Hide the facade once real widget is loaded
        setTimeout(() => {
          const facade = document.getElementById('chat-widget-facade');
          if (facade) {
            facade.style.display = 'none';
          }
        }, 500);
      } else {
        setError('Failed to load chat');
        logger.error('ChatWidget: Failed to load Tawk.to', null, 'chat');
      }
    } catch (err) {
      setError('Chat unavailable');
      logger.error('ChatWidget: Error loading Tawk.to', { error: err }, 'chat');
    } finally {
      setIsLoading(false);
    }
  }, [isLoaded, isLoading]);

  // Handle facade click
  const handleFacadeClick = useCallback(async () => {
    logger.debug('ChatWidget: Facade clicked, loading Tawk.to', null, 'chat');
    
    // Load the script first
    await loadTawkTo();
    
    // Then maximize the chat
    if (isLoaded || tawkToLoader.isTawkAvailable()) {
      tawkToLoader.maximize();
      setIsMinimized(false);
    }
  }, [loadTawkTo, isLoaded]);

  // Auto-load after delay for user engagement
  useEffect(() => {
    if (!shouldShowChat) return;

    // Set up auto-load timer (10 seconds after component mount)
    const autoLoadTimer = setTimeout(() => {
      logger.debug('ChatWidget: Auto-loading Tawk.to after delay', null, 'chat');
      loadTawkTo();
    }, 10000);  // Defer to 10s - after LCP measurement window

    // Also load on first user interaction (as backup)
    const handleFirstInteraction = () => {
      logger.debug('ChatWidget: Loading Tawk.to on first interaction', null, 'chat');
      loadTawkTo();
      // Remove listeners after first interaction
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('scroll', handleFirstInteraction);
    };

    // Add interaction listeners
    document.addEventListener('click', handleFirstInteraction, { once: true });
    document.addEventListener('scroll', handleFirstInteraction, { once: true });

    // Cleanup
    return () => {
      clearTimeout(autoLoadTimer);
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('scroll', handleFirstInteraction);
    };
  }, [shouldShowChat, loadTawkTo]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      // Optional: Could call tawkToLoader.cleanup() here if you want to remove the script
      // when navigating away, but typically we keep it loaded once initialized
    };
  }, []);

  // Don't render on excluded routes
  if (!shouldShowChat) {
    return null;
  }

  // Don't show facade if script is already loaded
  if (isLoaded && tawkToLoader.isTawkAvailable()) {
    return null; // Real Tawk.to widget is handling display
  }

  // Render lightweight facade
  return (
    <div
      id="chat-widget-facade"
      onClick={handleFacadeClick}
      style={{
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        width: '60px',
        height: '60px',
        borderRadius: '50%',
        backgroundColor: '#03a84e',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        transition: 'transform 0.2s, box-shadow 0.2s',
        zIndex: 9999,
        fontSize: '24px',
        fontWeight: 'bold',
        userSelect: 'none',
        ...(isLoading && { opacity: 0.7 }),
        ...(error && { backgroundColor: '#dc3545' })
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'scale(1.1)';
        e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.2)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'scale(1)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
      }}
      title={error || (isLoading ? 'Loading chat...' : 'Click to chat with us')}
      aria-label="Open chat"
      role="button"
      tabIndex={0}
      onKeyPress={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleFacadeClick();
        }
      }}
    >
      {/* Chat icon or loading spinner */}
      {isLoading ? (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            animation: 'spin 1s linear infinite'
          }}
        >
          <style>
            {`
              @keyframes spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
              }
            `}
          </style>
          <path d="M21 12a9 9 0 11-6.219-8.56" />
        </svg>
      ) : error ? (
        '!'
      ) : (
        <svg
          width="28"
          height="28"
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>
        </svg>
      )}
    </div>
  );
};

export default ChatWidget;